import React, { CSSProperties } from 'react';
import {
  Button,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import moment from 'moment';
import { Link, useParams, useRouteMatch } from 'react-router-dom';

import config from 'config';
import {
  AffiliationsListQuery,
  useDeleteEmployeeAffiliationMutation,
  AffiliationsListDocument,
} from 'generated/graphql';
import { primaryBlue } from 'utils/color-styles';
import { PureRender } from 'utils/component-helpers';
import Campaign from 'entities/campaign/model';
import { TableBox } from 'retired/Box';
import CancelButtonIcon from 'assets/icons/cancel-button.svg';
import EditIcon from 'assets/icons/edit.svg';
import DialogButton from 'retired/DialogButton';
import Img from 'retired/Img';

import { AddAffiliationButton } from './AddAffiliationButton';
import { getAffiliationType, getAffiliationBoardPosition } from './getAffiliationType';

const styleForDate: CSSProperties = {
  whiteSpace: 'nowrap',
};

interface HeaderColumnInterface {
  id: string;
  label: string;
  sorted?: boolean;
  campaigns: Campaign[];
}

const ProgressOverlay = (): JSX.Element => (
  <div
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, .6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      zIndex: 100,
    }}
  >
    <CircularProgress />
  </div>
);

const AffiliationsTable = ({ data }: { data: AffiliationsListQuery }): JSX.Element => {
  const { affiliations } = data;
  const match = useRouteMatch();
  const { id: userId } = useParams<{ id: string }>();

  const [deleteEmployeeAffiliationMutation, { loading }] = useDeleteEmployeeAffiliationMutation();

  const handleDeleteAffiliation = (id: string): void => {
    deleteEmployeeAffiliationMutation({
      variables: { deleteEmployeeAffiliationId: id },
      update: (cache, { data: updatedData }) => {
        const affiliationsList = cache.readQuery<AffiliationsListQuery['data']>({
          query: AffiliationsListDocument,
          variables: { userId },
        });
        const updatedAffiliations = affiliationsList?.affiliations.filter(
          (affiliation) => affiliation.id !== updatedData?.deleteEmployeeAffiliation?.id
        );
        cache.writeQuery({
          query: AffiliationsListDocument,
          variables: { userId },
          data: {
            affiliations: updatedAffiliations,
          },
        });
      },
    });
  };

  const columns: any[] = [
    {
      id: 'nonprofit',
      label: 'Nonprofit name',
    },
    {
      id: 'affiliation',
      label: 'Affiliation',
    },
    {
      id: 'boardPosition',
      label: 'Board Position',
    },
    {
      id: 'startingAt',
      label: 'Start date',
    },
    {
      id: 'endingAt',
      label: 'End date',
    },
    {
      id: 'actions',
      label: 'Actions',
    },
  ];

  const displayDate = (date?: Date): string => (date ? moment(date).format('MMM DD, YYYY') : '-');

  return (
    <div style={{ marginTop: 30 }}>
      <div
        style={{
          padding: 10,
          marginBottom: 10,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          style={{
            fontSize: 18,
            fontWeight: 'bold',
            padding: 10,
          }}
        >
          {`Affiliations to Nonprofits (${affiliations.length} affiliations)`}
        </Typography>
        <AddAffiliationButton />
      </div>

      <TableBox style={{ maxHeight: 350 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.filter(Boolean).map((item: HeaderColumnInterface) => (
                <TableCell key={item.id} size="medium" style={styleForDate}>
                  {item.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody style={{ position: 'relative' }}>
            {loading && <ProgressOverlay />}
            {affiliations?.map((affiliation) => (
              <TableRow key={affiliation.id}>
                <TableCell size="medium">
                  <Avatar
                    alt={affiliation.Nonprofit.name}
                    src={affiliation.Nonprofit?.mainPicture}
                    style={{
                      width: 32,
                      height: 32,
                      float: 'left',
                      marginRight: 10,
                    }}
                  />
                  <Typography
                    style={{
                      lineHeight: '32px',
                      cursor: 'pointer',
                      color: primaryBlue,
                    }}
                    onClick={(e): void => {
                      e.preventDefault();
                      window.open(
                        `${config.clientUrl}/organization/${affiliation.nonprofitId}`,
                        '_blank'
                      );
                    }}
                  >
                    {affiliation.Nonprofit.name}
                  </Typography>
                </TableCell>
                <TableCell size="medium">
                  {getAffiliationType(affiliation.affiliationType)}
                </TableCell>
                <TableCell size="medium">
                  {getAffiliationBoardPosition(affiliation.boardPosition) || 'N/A'}
                </TableCell>
                <TableCell size="medium" style={styleForDate}>
                  {displayDate(affiliation.startingAt)}
                </TableCell>
                <TableCell size="medium" style={styleForDate}>
                  {affiliation.endingAt ? displayDate(affiliation.endingAt) : 'Ongoing'}
                </TableCell>
                <TableCell size="medium">
                  <div style={{ display: 'flex' }}>
                    <div style={{ marginRight: 15 }}>
                      <Button
                        component={Link}
                        to={`${match.url}/affiliations/edit/${affiliation.id}`}
                      >
                        Edit
                        <Img
                          style={{
                            marginLeft: 10,
                            width: 10,
                          }}
                          src={EditIcon}
                          alt="Edit"
                        />
                      </Button>
                    </div>
                    <div>
                      <DialogButton
                        title="Remove affiliation"
                        text="Are you sure you want to remove this affiliation?"
                        confirmLabel="Confirm remove"
                        cancelLabel="Go back"
                        handleConfirm={(): void => handleDeleteAffiliation(affiliation.id)}
                      >
                        <div>
                          Remove
                          <Img
                            style={{
                              marginLeft: 10,
                              width: 10,
                            }}
                            src={CancelButtonIcon}
                            alt="Cancel"
                          />
                        </div>
                      </DialogButton>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableBox>
    </div>
  );
};

export default PureRender(AffiliationsTable);
